import initCookie from 'src/internal/cookie/initCookie';
// TODO: pending to be properly migrated away from using test store.
import {readCookies} from 'src/test/store';

import type {IReports} from '@sail/observability';
import type {
  RemoveCookieOptions,
  SetCookieOptions,
} from '@stripe-internal/stripe-cookies';
import type {DataConfig} from 'src/internal/config/types';
import type {CookieEventCallback} from 'src/internal/cookie/createCookieEventListener';

type BrowserStorageConfig = DataConfig['browserStorage'];

/**
 * Imperative API for managing cookies.
 *
 * Allows reading/writing cookies while honoring a user's cookie permissions
 * and provides methods for listening to cookie events.
 *
 * When accesing cookies inside a React Component, better use the
 * [useCookie](./useCookie.md) hook.
 *
 */
export default {
  /**
   * Reads the value of the specified cookie.
   */
  get(
    /** The cookie config object for `@sail/data`. */
    config: BrowserStorageConfig,
    /** The name of the cookie to be read. */
    name: string,
    /** Reports observability Instance for reporting internal errors/warnings */
    reports: IReports,
  ): string | null {
    const result = initCookie(config, reports).stripeCookie.get(name);

    readCookies.get(name)?.forEach((callback) => {
      callback(name, result);
    });

    return result;
  },

  /**
   * Attempts to write the specified value to the specified cookie. This will
   * only succeed if it complies with the user permissions.
   */
  set(
    /** The config object for `@sail/data`. */
    config: BrowserStorageConfig,
    /** The name of the cookie to be set. */
    name: string,
    /**
     * The new value to be set. If value is `null`, the cookie will be
     * deleted.
     */
    value: string | null,
    /** Reports observability Instance for reporting internal errors/warnings */
    reports: IReports,
    /**
     * Attributes of the cookie to be set. Currently only supports `domain` and
     * `lifetime` attributes.
     */
    options?: SetCookieOptions,
  ): Promise<boolean> {
    if (value == null) {
      // Returning a promise to match the return value of the `set` function.
      return Promise.resolve(
        initCookie(config, reports).stripeCookie.remove(name, options),
      );
    }

    return initCookie(config, reports).stripeCookie.set(name, value, options);
  },

  /**
   * Deletes the value for the specified cookie.
   */
  remove(
    /** The config object for `@sail/data`. */
    config: BrowserStorageConfig,
    /** The name of the cookie to be removed. */
    name: string,
    /** Reports observability Instance for reporting internal errors/warnings */
    reports: IReports,
    /**
     * Attributes of the cookie to be deleted. Currently only supports
     * `domain` attributes.
     */
    options?: RemoveCookieOptions,
  ): boolean {
    return initCookie(config, reports).stripeCookie.remove(name, options);
  },

  /**
   * Listens to cookie events. The method accepts a callback that will be
   * called whenever a cookie value is updated or a cookie is deleted.
   */
  subscribe(
    /** The config object for `@sail/data`. */
    config: BrowserStorageConfig,
    /** The function that will be called every time a cookie is set or removed. */
    callback: CookieEventCallback,
    /** Reports observability Instance for reporting internal errors/warnings */
    reports: IReports,
  ): void {
    initCookie(config, reports).eventListener.addListener(callback);
  },

  /**
   * Unsubscribes from cookie events. The callback should be the same one
   * used for subscribing to cookie events.
   */
  unsubscribe(
    /** The config object for `@sail/data`. */
    config: BrowserStorageConfig,
    /** The callback you want to unsubscribe. */
    callback: CookieEventCallback,
    /** Reports observability Instance for reporting internal errors/warnings */
    reports: IReports,
  ): void {
    initCookie(config, reports).eventListener.removeListener(callback);
  },
};
